import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Clock, CheckCircle, User, Check } from 'lucide-react';
import { getMyActivities, getActivity, updateActivity } from '../../api';

const statusStyles = {
  'Pending': { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  'InProgress': { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
  'Completed': { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
};

const priorityColors = {
  'Low': '#6b7280',
  'Medium': '#f59e0b',
  'High': '#ef4444',
  'Urgent': '#dc2626',
};

export default function MyActivity() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [completingId, setCompletingId] = useState(null);

  const load = () => {
    setLoading(true);
    getMyActivities()
      .then(res => {
        setData(res.data || []);
        setFiltered(res.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let result = [...data];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.name?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
      );
    }
    if (filterStatus) result = result.filter(d => d.status === filterStatus);
    setFiltered(result);
  }, [search, filterStatus, data]);

  const handleComplete = async (id) => {
    if (!window.confirm('Tandai activity ini sebagai selesai?')) return;
    setCompletingId(id);
    try {
      const res = await getActivity(id);
      const d = res.data;
      await updateActivity(id, {
        name: d.name,
        description: d.description || '',
        category: d.category,
        priority: d.priority,
        tanggal: d.tanggal,
        start_time: d.start_time,
        end_time: d.end_time,
        bagian: d.bagian,
        status: 'Completed',
        attachment: d.attachment || '',
        assigned_to: d.assigned_to,
      });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal update status');
    } finally {
      setCompletingId(null);
    }
  };

  const total = data.length;
  const completed = data.filter(d => d.status === 'Completed').length;
  const inProgress = data.filter(d => d.status === 'InProgress').length;
  const pending = data.filter(d => d.status === 'Pending').length;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <User size={24} />
              My Activities
            </h2>
            <p className="page-desc">
              Activity yang ditugaskan kepada Anda ({filtered.length} dari {data.length})
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/activity/add')}
            style={{ background: '#059669', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={16} />
            <span>Add Activity</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 16px', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '0.7rem', color: '#166534', fontWeight: 500 }}>Total</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#166534' }}>{total}</div>
        </div>
        <div style={{ background: '#d1fae5', borderRadius: 10, padding: '12px 16px', border: '1px solid #a7f3d0' }}>
          <div style={{ fontSize: '0.7rem', color: '#065f46', fontWeight: 500 }}>Selesai</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#065f46' }}>{completed}</div>
        </div>
        <div style={{ background: '#dbeafe', borderRadius: 10, padding: '12px 16px', border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: '0.7rem', color: '#1e40af', fontWeight: 500 }}>Dalam Proses</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e40af' }}>{inProgress}</div>
        </div>
        <div style={{ background: '#fef3c7', borderRadius: 10, padding: '12px 16px', border: '1px solid #fde68a' }}>
          <div style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 500 }}>Pending</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#92400e' }}>{pending}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Cari activity anda..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 36px', borderRadius: 8,
              border: '1px solid #d1d5db', fontSize: '0.85rem', outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          style={filterSelectStyle}>
          <option value="">Semua Status</option>
          <option value="Pending">Pending</option>
          <option value="InProgress">In Progress</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: 16, border: '1px dashed #d1d5db' }}>
          <Clock size={48} style={{ color: '#9ca3af', marginBottom: 12, opacity: 0.5 }} />
          <h3 style={{ color: '#374151', marginBottom: 6, fontSize: '1.1rem' }}>Belum Ada Activity</h3>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Anda belum ditugaskan untuk activity apapun.</p>
          <button className="btn btn-primary" onClick={() => navigate('/activity/add')}
            style={{ marginTop: 16, background: '#059669', color: 'white', border: 'none' }}>
            <Plus size={16} /> Buat Activity Baru
          </button>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>Status</th>
                <th>Category</th>
                <th>Bagian</th>
                <th>Priority</th>
                <th>Date</th>
                <th>Dibuat Oleh</th>
                <th style={{ width: 100 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const st = statusStyles[d.status] || statusStyles['Pending'];
                return (
                  <tr key={d.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{d.name}</div>
                      {d.description && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem',
                        fontWeight: 600, background: st.bg, color: st.color
                      }}>
                        {d.status === 'InProgress' ? <Clock size={12} /> : d.status === 'Completed' ? <CheckCircle size={12} /> : <Clock size={12} />}
                        {st.label}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: '#374151' }}>{d.category || '-'}</td>
                    <td style={{ fontSize: '0.82rem' }}>{d.bagian || '-'}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: '0.72rem', fontWeight: 700, color: 'white',
                        background: priorityColors[d.priority] || '#6b7280'
                      }}>
                        {d.priority || 'Medium'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>{d.tanggal || '-'}</td>
                    <td style={{ fontSize: '0.82rem' }}>{d.user_name || '-'}</td>
                    <td>
                      {d.status !== 'Completed' ? (
                        <button
                          onClick={() => handleComplete(d.id)}
                          disabled={completingId === d.id}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '5px 12px', borderRadius: 6, fontSize: '0.75rem',
                            fontWeight: 600, cursor: 'pointer',
                            border: '1px solid #059669', background: '#f0fdf4',
                            color: '#059669', transition: 'all 0.15s',
                            opacity: completingId === d.id ? 0.6 : 1
                          }}
                        >
                          <Check size={13} />
                          {completingId === d.id ? '...' : 'Selesaikan'}
                        </button>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const filterSelectStyle = {
  padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.85rem', outline: 'none', background: 'white', minWidth: 140
};
